import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { createBooking, getBookingQuote, getItem, recordItemEvent } from '../api';
import ListingImage from '../components/ListingImage';
import { useAuth } from '../auth';
import { useWishlist } from '../hooks/useWishlist';
import FormWizard from '../components/FormWizard';
import AuthToBook from '../components/AuthToBook';
import CancellationPolicy from '../components/CancellationPolicy';
import AddressConfirmModal from '../components/AddressConfirmModal';
import AddressAutocomplete from '../components/AddressAutocomplete';
import PriceBreakdown from '../components/PriceBreakdown';
import StarRating from '../components/StarRating';
import ItemRatings from '../components/ItemRatings';
import RelatedItems from '../components/RelatedItems';
import { PolicyBadges, SpecificationsTable } from '../components/ItemPoliciesAndSpecs';
import JewelleryMetalNotice from '../components/JewelleryMetalNotice';
import { JewellerySpecView, jewelleryHighlightTags } from '../components/JewellerySpecFields';
import PeriodBookingDates from '../components/PeriodBookingDates';

const emptyAddress = { line1: '', line2: '', city: '', area: '', pincode: '' };

// v5_fix_issues #8 - name / email / phone come from the account, not the form.
const emptyForm = {
  startDate: '',
  endDate: '',
  periodCount: 1, // week listings: number of whole weeks (PeriodBookingDates)
  notes: '',
  // v6 stepper forms - up-front-payment choice (schema always supported it).
  payNow: 'full',
  deliveryAddress: emptyAddress
};

export default function ItemDetail() {
  const { id } = useParams();
  const location = useLocation();
  // homepage/user_experience.md #3 - the filtered/sorted browse URL the customer came from.
  const backToListings = location.state?.from || '/';
  const { user, isAuthenticated } = useAuth();
  const { has: isWishlisted, toggle: toggleWishlist } = useWishlist();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [showAddressConfirm, setShowAddressConfirm] = useState(false); // Story 16
  const [ackPolicy, setAckPolicy] = useState(false); // v6 - cancellation-policy acknowledgement

  // delivery_distance_pricing.md - live price breakdown (incl. the distance-based delivery
  // charge) once dates are set. Re-quoted, debounced, whenever the dates or delivery address
  // change - the delivery charge only resolves once the address carries coordinates.
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getItem(id)
      .then((data) => {
        if (!cancelled) setItem(data);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Item not found.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Records a "detail opened" engagement event feeding the recommended-feed ranking (story 1.3).
  // Fired once per detail-page visit, independent of whether the load above succeeds/fails.
  useEffect(() => {
    recordItemEvent(id, 'view');
  }, [id]);

  // Pre-fill the form for logged-in customers so booking is a single step. Story 16: also
  // prefills the delivery address from the customer's last-used one (RentalUser.savedAddress,
  // returned on the user object at login/signup) so a returning customer doesn't retype it -
  // still shown back to them in the confirm popup below in case it's since changed.
  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        deliveryAddress: (prev.deliveryAddress?.line1 || !user.savedAddress?.line1)
          ? prev.deliveryAddress
          : { ...emptyAddress, ...user.savedAddress }
      }));
    }
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated || !form.startDate || !form.endDate) {
      setQuote(null);
      return undefined;
    }
    setQuoteLoading(true);
    const handle = setTimeout(() => {
      getBookingQuote({
        itemId: id,
        startDate: form.startDate,
        endDate: form.endDate,
        deliveryAddress: form.deliveryAddress,
      })
        .then(setQuote)
        .catch(() => setQuote(null))
        .finally(() => setQuoteLoading(false));
    }, 400);
    return () => clearTimeout(handle);
  }, [id, isAuthenticated, form.startDate, form.endDate, form.deliveryAddress]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleAddressChange = (next) => {
    setForm((prev) => ({ ...prev, deliveryAddress: next }));
  };

  // Story 16 - the address form itself just needs to be filled in; submitting the booking request
  // happens one step later, from AddressConfirmModal's "Confirm & submit booking", once the
  // customer has explicitly confirmed (or corrected) the address in the popup.
  const handleOpenAddressConfirm = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSubmitError('');
    if (!user?.phone) {
      setSubmitError('Add a phone number to your account before booking.');
      return;
    }
    setShowAddressConfirm(true);
  };

  const handleConfirmAddressAndSubmit = async (confirmedAddress) => {
    setSubmitting(true);
    setSubmitError('');
    try {
      // periodCount is a UI-only helper for the week/month date picker (PeriodBookingDates) - the
      // server derives everything from startDate/endDate, so it's not part of the booking payload.
      const { periodCount, ...bookingForm } = form;
      const res = await createBooking({ ...bookingForm, deliveryAddress: confirmedAddress, itemId: id, guests: 1 });
      setConfirmation(res);
      setShowAddressConfirm(false);
    } catch (err) {
      setSubmitError(err.response?.data?.msg || 'Unable to submit booking. Please try again.');
      setShowAddressConfirm(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Spinner animation="border" role="status" />;
  if (loadError) {
    return (
      <Alert variant="light" className="mt-3">
        <p className="mb-2">{loadError} It may have been booked or taken down.</p>
        <Button as={Link} to={backToListings} variant="primary">Browse all pieces</Button>
      </Alert>
    );
  }
  if (!item) return null;

  const minPaymentRequired = !!(item.policies?.minBookingAmount || item.policies?.minBookingPercent);

  const identityBlock = (
    <div className="mb-3 small">
      <div><span className="text-muted">Name:</span> {user?.name}</div>
      <div><span className="text-muted">Email:</span> {user?.email}</div>
      <div>
        <span className="text-muted">Phone:</span>{' '}
        {user?.phone ? user.phone : <Link to="/account">Add a phone number to your account</Link>}
      </div>
    </div>
  );

  // v6 stepper forms (Part D / §D3) - the booking form is a 3-step wizard in the right column.
  const bookingSteps = [
    {
      key: 'dates',
      title: 'Dates',
      validate: (v) => {
        if (!v.startDate) return { startDate: 'Pick a start date.' };
        if (!v.endDate) return { endDate: 'Pick an end date.' };
        if (v.endDate < v.startDate) return { endDate: 'End date cannot be before the start date.' };
        return null;
      },
      render: () => (
        // v8 stock_and_availability - a live calendar (backed by GET /availability) instead of
        // plain date inputs. PeriodBookingDates swaps to a start-date + "how many weeks" picker
        // when the listing is priced per week (whole-period rule).
        <PeriodBookingDates item={item} form={form} setForm={setForm} resourceId={item._id} />
      )
    },
    {
      key: 'address',
      title: 'Delivery address',
      validate: (v) => {
        const a = v.deliveryAddress || {};
        const missing = ['line1', 'city', 'pincode'].filter((f) => !String(a[f] || '').trim());
        return missing.length ? { address: 'Fill in address line 1, city, and pincode. Pick from the suggestions so we can price delivery.' } : null;
      },
      render: () => (
        <>
          <AddressAutocomplete
            value={form.deliveryAddress}
            onChange={handleAddressChange}
            idPrefix="booking-address"
            required
          />
          <PriceBreakdown quote={quote} loading={quoteLoading && !quote} className="mt-3" />
        </>
      )
    },
    {
      key: 'review',
      title: 'Review & pay',
      validate: () => (ackPolicy ? null : { ack: 'Please acknowledge the cancellation policy before continuing.' }),
      render: () => (
        <>
          {identityBlock}
          <CancellationPolicy itemId={item._id} />
          {minPaymentRequired && (
            <Form.Group className="mb-3">
              <Form.Label>Pay now</Form.Label>
              <Form.Check
                type="radio" name="payNow" id="payNow-full" label="Pay the full amount now"
                checked={form.payNow === 'full'} onChange={() => setForm((p) => ({ ...p, payNow: 'full' }))}
              />
              <Form.Check
                type="radio" name="payNow" id="payNow-minimum"
                label={`Pay the minimum now${quote?.minPaymentAmount ? ` (₹${quote.minPaymentAmount})` : ''} - balance due later`}
                checked={form.payNow === 'minimum'} onChange={() => setForm((p) => ({ ...p, payNow: 'minimum' }))}
              />
            </Form.Group>
          )}
          <PriceBreakdown quote={quote} loading={quoteLoading && !quote} className="mb-3" />
          <Form.Group className="mb-3" controlId="booking-notes">
            <Form.Label>Notes</Form.Label>
            <Form.Control as="textarea" rows={2} value={form.notes} onChange={handleChange('notes')} />
          </Form.Group>
          <Form.Check
            type="checkbox" id="ack-policy" className="mb-2"
            label="I have read and accept the cancellation policy."
            checked={ackPolicy} onChange={(e) => setAckPolicy(e.target.checked)}
          />
          <p className="text-muted small mb-0">
            The next step lets you review and correct the delivery address, then confirm.
          </p>
        </>
      )
    }
  ];

  return (
    <div>
      <Link to={backToListings} className="btn btn-primary btn-sm mb-3 d-inline-flex align-items-center gap-2">
        <span aria-hidden="true" style={{ fontSize: '1.1em', lineHeight: 1 }}>←</span> Back to listings
      </Link>

      {!isAuthenticated && !confirmation && (
        <AuthToBook as="banner" action="request a booking" />
      )}

      <Row className="g-4">
        <Col md={6}>
          {item.images?.[0] && (
            <ListingImage src={item.images[0]} alt={item.name} eager sizes="(max-width: 768px) 100vw, 50vw" className="w-100 rounded shadow-sm" style={{ maxHeight: 360, objectFit: 'cover' }} />
          )}
          <h2 className="mt-3">{item.name}</h2>
          <p className="text-muted">
            {item.category} &middot; {item.location}
          </p>
          <p>{item.description}</p>
          {item.ratingCount > 0 && (
            <div className="mb-2">
              <StarRating value={item.ratingAvg} count={item.ratingCount} size="1.15rem" />
            </div>
          )}
          <div className="mb-2 d-flex flex-wrap gap-1">
            {jewelleryHighlightTags(item).slice(0, 5).map((tag) => (
              <Badge bg="secondary" key={tag}>{tag}</Badge>
            ))}
          </div>
          <h4>
            ₹{item.price}/{item.priceUnit}
          </h4>
          <PolicyBadges policies={item.policies} />
          <JewellerySpecView item={item} />
          <SpecificationsTable specifications={item.specifications} />
          <JewelleryMetalNotice />
        </Col>

        <Col md={6}>
          {confirmation ? (
            <Alert variant="success">
              <strong>{confirmation.msg}</strong>
              <p className="mb-0 mt-2">
                Track it any time on <Link to="/my-bookings">My Bookings</Link>.
              </p>
            </Alert>
          ) : !isAuthenticated ? (
            // fix_issues.md #10 - the booking API now requires a logged-in account (no more guest
            // checkout), so the form itself is gated the same way rather than letting a guest fill
            // it in and only find out from a 401 on submit. <AuthToBook> links carry the current
            // path so Login/Signup return the visitor right back here (v6 UX).
            <AuthToBook action="request a booking" />
          ) : item.bookingAvailability?.canBook === false ? (
            <Alert variant="light">{item.bookingAvailability.message}</Alert>
          ) : item.status !== 'available' ? (
            <Alert variant="light">This piece is currently unavailable.</Alert>
          ) : (
            <div>
              <h4>Request to book</h4>
              {submitError && <Alert variant="danger">{submitError}</Alert>}
              <FormWizard
                steps={bookingSteps}
                values={form}
                onChange={setForm}
                onSubmit={handleOpenAddressConfirm}
                submitting={submitting}
                submitLabel="Preview & confirm booking"
              />
            </div>
          )}
        </Col>
      </Row>

      <ItemRatings itemId={item._id} />
      <RelatedItems itemId={item._id} category={item.category} />
      <div className="mt-4 d-flex gap-2"><Button variant={isWishlisted(item._id) ? 'danger' : 'outline-danger'} onClick={() => toggleWishlist(item._id)}>{isWishlisted(item._id) ? '♥ Saved to wishlist' : '♡ Add to wishlist'}</Button><Link to={backToListings} className="btn btn-primary d-inline-flex align-items-center gap-2"><span aria-hidden="true">←</span> Back to listings</Link></div>

      <AddressConfirmModal
        show={showAddressConfirm}
        address={form.deliveryAddress}
        submitting={submitting}
        itemId={id}
        startDate={form.startDate}
        endDate={form.endDate}
        onConfirm={handleConfirmAddressAndSubmit}
        onHide={() => setShowAddressConfirm(false)}
      />
    </div>
  );
}
